/* eslint-disable no-case-declarations */
/* eslint-disable object-curly-newline */
/* eslint-disable no-param-reassign */
import { isEmpty, isNull } from 'lodash';
import { SecurityModel } from 'api';
import {
  CUMULATIVE_DIVIDENDS_ROW_NUMBER,
  INVESTMENT_DATE_ALIAS,
  SECURITY_NAME_ALIAS,
  SECURITY_TYPE_ALIAS,
  SECURITY_TYPE_ROW_NUMBER,
  SENIORITY_ALIAS,
  SHARES_OUTSTANDING_ALIAS,
  UNDERLYING_SECURITY_ALIAS,
} from 'common/constants/cap-table';
import * as validationMessages from 'common/constants/messages/validations';
import { OPTION, PREFERRED_STOCK as PREFERRED, WARRANT } from 'common/constants/securityTypes';
import { FundOwnershipDetail } from 'common/types/fundOwnership';
import { TableData } from 'common/types/scalarSpreadsheet';
import { ICell } from 'models/interfaces/spreadsheet-types/core-types';
import { toString } from 'utilities';
import { getPreferredSecurities } from './getOptionsForSelect';
import { CapTableCell } from '../components/captable-table/CaptableTable.props';
import * as validationsConstants from '../data/validationsConstants';

const customValidations = ({
  cell,
  parsedColumns,
  tableData,
  addFeedback,
  addFeedbackFromCell,
  removeFeedbackFromCell,
}: {
  cell: ICell,
  parsedColumns: Record<string, ICell>,
  tableData: TableData<CapTableCell> & { fund_ownership: { fund_ownership_detail: FundOwnershipDetail[] } },
  addFeedback: (message: string) => void,
  addFeedbackFromCell: (cell: ICell, message: string) => void,
  removeFeedbackFromCell: (cell: ICell, message: string) => void,
}) => {
  const { alias, value, defaultValue, columnLegend, tooltipMessages } = cell;

  const { columns } = tableData;
  const preferredSecurities = getPreferredSecurities({ columns } as unknown as { columns: SecurityModel[] });
  const isSecurityType = (type: number) => {
    const securityType = parsedColumns[columnLegend! + SECURITY_TYPE_ROW_NUMBER];

    if (type && securityType?.value) {
      if (securityType.value.toString() === type.toString()) {
        return true;
      }
    }
    return false;
  };

  const isCumulativeDividendsYes = () => {
    const dividendsCell = parsedColumns[columnLegend! + CUMULATIVE_DIVIDENDS_ROW_NUMBER];
    return dividendsCell.value === '1';
  };

  const validateSecurityNameUniqueness = () => {
    // is the current cell a duplicate
    const currentNameValue = cell.value;

    // designed to stop iterating when the first duplicate is found
    const duplicateCell = Object.values(parsedColumns).find(tCell => {
      const isNameCell = tCell.alias === SECURITY_NAME_ALIAS;
      const isValidCellTypeForComparison = typeof tCell.value === 'string' && typeof currentNameValue === 'string';
      const isCurrentCell = tCell.key === cell.key;

      if (!isNameCell || !isValidCellTypeForComparison || isCurrentCell) return false;

      const securityNameMatches = tCell.value?.toString().toLowerCase() === currentNameValue.toLowerCase();

      return securityNameMatches;
    });

    const duplicateCellFound = !!duplicateCell;
    if (duplicateCellFound) {
      addFeedback(validationMessages.SECURITY_NAME_UNIQUE);
    } else {
      removeFeedbackFromCell(cell, validationMessages.SECURITY_NAME_UNIQUE);
    }
  };

  switch (alias) {
    case SECURITY_NAME_ALIAS: // 0
      if (isNull(value) || isEmpty(toString(value as string).trim())) {
        addFeedback('Create a name for this security.');
      } else if (value === defaultValue) {
        addFeedback(validationMessages.REPLACE_DEFAULT_VALUE);
      } else {
        removeFeedbackFromCell(cell, validationMessages.REPLACE_DEFAULT_VALUE);
        validateSecurityNameUniqueness();
      }

      break;
    case INVESTMENT_DATE_ALIAS: // 02
      if (!isCumulativeDividendsYes() && isSecurityType(PREFERRED)) {
        // remove required feedback from the investment date cell
        removeFeedbackFromCell(cell, validationMessages.IS_REQUIRED_ERROR);
      } else if (isCumulativeDividendsYes() && isSecurityType(PREFERRED) && !value) {
        addFeedbackFromCell(cell, validationMessages.IS_REQUIRED_ERROR);
      }
      break;
    case SECURITY_TYPE_ALIAS: // 03
      if (Number(value) <= 0) {
        addFeedback(validationMessages.SELECT_VALID_SECURITY);
      } else {
        removeFeedbackFromCell(cell, validationMessages.SELECT_VALID_SECURITY);
      }
      if (tooltipMessages?.length && tooltipMessages.length > 0) {
        addFeedback(tooltipMessages[0]);
      } else {
        removeFeedbackFromCell(cell, tooltipMessages?.[0] as string);
      }
      break;
    case SHARES_OUTSTANDING_ALIAS: // 05
      if (isSecurityType(PREFERRED)) {
        /*
          Validate if the sum of shares in the related columns in fund ownership,
          are less than the shares outstanding
        */
        const fundOwnershipColumns = tableData?.fund_ownership?.fund_ownership_detail
          ? tableData.fund_ownership.fund_ownership_detail
          : [];

        const relatedFundColumns = fundOwnershipColumns.filter(
          column => column.security?.toString() === cell.columnId?.toString()
        );

        // Get the total shares of the related fund ownership columns
        if (relatedFundColumns?.length) {
          const totalFundOwnershipShares = relatedFundColumns.reduce((acc, curr) => acc + Number(curr.shares), 0);

          if (cell.value && Number(cell.value) < totalFundOwnershipShares) {
            addFeedback(validationMessages.LESS_SHARES_IN_CAPTABLE_THAN_FUND_OWNERSHIP);
          }
        }
      }
      break;

    case UNDERLYING_SECURITY_ALIAS: // 12
      if ([0, '0', null].includes(value as string)) {
        if (isSecurityType(OPTION)) {
          addFeedback('Please select the security that this option purchases when exercised.');
        }

        if (isSecurityType(WARRANT)) {
          addFeedback('Please select the security that this warrant purchases when exercised.');
        }
      }
      break;
    case SENIORITY_ALIAS: // 15
      if (value === 'SELECT PRIORITY' || (value && Number(value) > preferredSecurities.length)) {
        addFeedback(validationsConstants.SELECT_VALID_LIQUIDATION_PRIORITY);
      } else {
        removeFeedbackFromCell(cell, validationsConstants.SELECT_VALID_LIQUIDATION_PRIORITY);
      }
      break;
    default:
      break;
  }
};

export default customValidations;
